import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Clock, Calendar, TrendingDown, AlertCircle, Filter, Download, RefreshCw } from 'lucide-react';

// ✅ Cookie sent automatically — no Authorization header needed
const defaultFetchOptions = {
  credentials: 'include',
  headers: { 'accept': '*/*' }
};

const FormApiService = {
  API_BASE_URL: 'https://apps.genus.in/GenOpsAPI/api',
  //API_BASE_URL: 'http://localhost:5134/api',

  async getPCBLiveProjects(serverId, forDate) {
    try {
      const response = await fetch(
        `${this.API_BASE_URL}/PCB/live-projects/daily?serverId=${serverId}&forDate=${forDate}`,
        { ...defaultFetchOptions }
      );
      if (!response.ok) throw new Error('Failed to fetch PCB projects');
      return await response.json();
    } catch (error) {
      console.error('Error fetching PCB projects:', error);
      throw error;
    }
  },

  async getPCBRunningLines(serverId, projCode, forDate) {
    try {
      const response = await fetch(
        `${this.API_BASE_URL}/PCB/running-lines/daily?serverId=${serverId}&projCode=${projCode}&forDate=${forDate}`,
        { ...defaultFetchOptions }
      );
      if (!response.ok) throw new Error('Failed to fetch PCB running lines');
      return await response.json();
    } catch (error) {
      console.error('Error fetching PCB running lines:', error);
      throw error;
    }
  },

  async getEMALiveProjects(serverId) {
    try {
      const response = await fetch(
        `${this.API_BASE_URL}/Setting/LiveProjectList?serverID=${serverId}`,
        { ...defaultFetchOptions }
      );
      if (!response.ok) throw new Error('Failed to fetch EMA projects');
      return await response.json();
    } catch (error) {
      console.error('Error fetching EMA projects:', error);
      throw error;
    }
  },

  async getEMARunningLines(serverId, projCode, fromDt, toDt) {
    try {
      const params = new URLSearchParams({ serverId: serverId.toString(), projCode, fromDt, toDt });
      const response = await fetch(
        `${this.API_BASE_URL}/Setting/RunningLines?${params}`,
        { ...defaultFetchOptions }
      );
      if (!response.ok) throw new Error('Failed to fetch EMA running lines');
      return await response.json();
    } catch (error) {
      console.error('Error fetching EMA running lines:', error);
      throw error;
    }
  },

  async getProductionGapsRange(serverId, projCode, lineNumber, shift, startDate, endDate, area, minGapMinutes) {
    try {
      const params = new URLSearchParams({
        serverId: serverId.toString(), projCode,
        lineNumber: lineNumber.toString(), shift,
        startDate, endDate, area
      });
      if (minGapMinutes && parseInt(minGapMinutes) > 0) {
        params.append('minGapMinutes', minGapMinutes.toString());
      }
      const response = await fetch(
        `${this.API_BASE_URL}/EntryFrom/gaps/range?${params}`,
        { ...defaultFetchOptions }
      );
      if (!response.ok) throw new Error('Failed to fetch production gaps');
      return await response.json();
    } catch (error) {
      console.error('Error fetching production gaps:', error);
      throw error;
    }
  }
};

// ── paste the entire DowntimeViewer component + styles below unchanged ──

const DowntimeViewer = () => {
  const [filters, setFilters] = useState({
    serverId: '',
    projCode: '',
    lineNumber: '',
    shift: '',
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    area: '',
    minGapMinutes: ''
  });

  const [downtimeData, setDowntimeData] = useState([]);
  const [projectList, setProjectList] = useState([]);
  const [lineList, setLineList] = useState([]);
  
  const [loading, setLoading] = useState({
    projects: false,
    lines: false,
    data: false
  });

  const [stats, setStats] = useState({
    totalDowntime: 0,
    incidentCount: 0,
    avgDuration: 0,
    longestDowntime: 0,
    totalTimeFrameMinutes: 0,
    timeFrameCount: 0
  });

  const ServerInfo = [
    { id: 1, name: 'Genus RND', serverIP: '192.10.10.4' },
    { id: 2, name: 'RCP Jaipur', serverIP: '10.141.61.40' },
    { id: 3, name: 'HDR 1101', serverIP: '10.133.100.21' },
    { id: 4, name: 'HDR 3000', serverIP: '10.134.1.21' },
    { id: 5, name: 'HDR 1100', serverIP: '10.133.1.22' },
    { id: 6, name: 'Guhawati', serverIP: '10.161.1.22' },
  ];

  const shiftOptions = ['A', 'B'];
  const areaOptions = ['PCB', 'EMA'];

  const getDisplayValue = (item, ...keys) => {
    if (typeof item === 'string' || typeof item === 'number') return String(item);
    if (typeof item === 'object' && item !== null) {
      for (const key of keys) {
        if (item[key] !== undefined && item[key] !== null) return String(item[key]);
      }
    }
    return '';
  };

  const getValueForSubmission = (item, ...keys) => {
    if (typeof item === 'string' || typeof item === 'number') return String(item);
    if (typeof item === 'object' && item !== null) {
      for (const key of keys) {
        if (item[key] !== undefined && item[key] !== null) return String(item[key]);
      }
    }
    return '';
  };

  useEffect(() => {
    const fetchProjects = async () => {
      if (!filters.serverId || !filters.area || !filters.startDate) {
        setProjectList([]);
        return;
      }

      setLoading(prev => ({ ...prev, projects: true }));
      try {
        let projects = [];
        const serverId = parseInt(filters.serverId);
        const forDate = filters.startDate.split('T')[0];

        if (filters.area === 'PCB') {
          projects = await FormApiService.getPCBLiveProjects(serverId, forDate);
        } else if (filters.area === 'EMA') {
          projects = await FormApiService.getEMALiveProjects(serverId);
        }

        setProjectList(Array.isArray(projects) ? projects : []);
        setFilters(prev => ({ ...prev, projCode: '', lineNumber: '' }));
        setLineList([]);
      } catch (error) {
        console.error('Error fetching projects:', error);
        setProjectList([]);
      } finally {
        setLoading(prev => ({ ...prev, projects: false }));
      }
    };

    fetchProjects();
  }, [filters.serverId, filters.area, filters.startDate]);

  useEffect(() => {
    const fetchLines = async () => {
      if (!filters.projCode || !filters.area || !filters.serverId || !filters.startDate || !filters.endDate) {
        setLineList([]);
        return;
      }

      setLoading(prev => ({ ...prev, lines: true }));
      try {
        let lines = [];
        const serverId = parseInt(filters.serverId);
        const fromDt = filters.startDate.split('T')[0];
        const toDt = filters.endDate.split('T')[0];

        if (filters.area === 'PCB') {
          lines = await FormApiService.getPCBRunningLines(serverId, filters.projCode, fromDt);
        } else if (filters.area === 'EMA') {
          lines = await FormApiService.getEMARunningLines(serverId, filters.projCode, fromDt, toDt);
        }

        setLineList(Array.isArray(lines) ? lines : []);
        setFilters(prev => ({ ...prev, lineNumber: '' }));
      } catch (error) {
        console.error('Error fetching lines:', error);
        setLineList([]);
      } finally {
        setLoading(prev => ({ ...prev, lines: false }));
      }
    };

    fetchLines();
  }, [filters.projCode, filters.area, filters.serverId, filters.startDate, filters.endDate]);

  const fetchDowntimeData = async () => {
    if (!filters.serverId || !filters.projCode || !filters.lineNumber || 
        !filters.shift || !filters.startDate || !filters.endDate || !filters.area) {
      alert('Please fill all required fields before fetching data');
      return;
    }

    setLoading(prev => ({ ...prev, data: true }));
    
    try {
      const serverId = parseInt(filters.serverId);
      const lineNumber = parseInt(filters.lineNumber);
      const minGap = filters.minGapMinutes === '' ? '' : Math.max(1, parseInt(filters.minGapMinutes) || 5);

      const data = await FormApiService.getProductionGapsRange(
        serverId,
        filters.projCode,
        lineNumber,
        filters.shift,
        filters.startDate,
        filters.endDate,
        filters.area,
        minGap
      );
      
      // Debug: Log the actual API response to see field names
      console.log('API Response:', data);
      if (data && data.length > 0) {
        console.log('First item structure:', data[0]);
        console.log('Available keys:', Object.keys(data[0]));
      }
      
      const processedData = Array.isArray(data)
        ? data.map((item, index) => {
            let startTime = null;
            let endTime = null;

            // API format: timeRange = "09:08 - 09:14", dayLabel = "2025-12-23"
            if (item.timeRange && item.dayLabel) {
              const parts = item.timeRange.split(' - ');
              if (parts.length === 2) {
                startTime = `${item.dayLabel} ${parts[0]}`;
                endTime   = `${item.dayLabel} ${parts[1]}`;
              }
            }

            const duration = parseFloat(item.gapMinutes || 0);

            console.log(`Item ${index + 1}:`, {
              startTime,
              endTime,
              duration,
              rawItem: item
            });

            return {
              id: index + 1,
              startTime,
              endTime,
              duration,
              reason: item.reason || item.Reason || 'Downtime',
              category: item.category || item.Category || 'Unknown',
              area: filters.area,
              shift: filters.shift,
              lineNumber
            };
          })
        : [];

      setDowntimeData(processedData);

      
      const totalMinutes = processedData.reduce((sum, item) => sum + item.duration, 0);
      const maxDuration = Math.max(...processedData.map(item => item.duration), 0);
      
      setStats({
        totalDowntime: totalMinutes,
        incidentCount: processedData.length,
        avgDuration: processedData.length > 0 ? Math.round(totalMinutes / processedData.length) : 0,
        longestDowntime: maxDuration,
        totalTimeFrameMinutes: totalMinutes,
        timeFrameCount: processedData.length
      });
      
    } catch (error) {
      console.error('Error fetching downtime data:', error);
      alert(`Error fetching downtime data: ${error.message}`);
      setDowntimeData([]);
    } finally {
      setLoading(prev => ({ ...prev, data: false }));
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch (e) {
      return 'N/A';
    }
  };

  const chartDataByReason = downtimeData.reduce((acc, item) => {
    const existing = acc.find(x => x.reason === item.reason);
    if (existing) {
      existing.duration += item.duration;
      existing.count += 1;
    } else {
      acc.push({ reason: item.reason, duration: item.duration, count: 1 });
    }
    return acc;
  }, []);

  const chartDataByShift = downtimeData.reduce((acc, item) => {
    const existing = acc.find(x => x.shift === item.shift);
    if (existing) {
      existing.duration += item.duration;
    } else {
      acc.push({ shift: item.shift, duration: item.duration });
    }
    return acc;
  }, []);

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  const exportToCSV = () => {
    if (downtimeData.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = ['#', 'Start Time', 'End Time', 'Duration (min)', 'Area', 'Shift', 'Line'];
    const rows = downtimeData.map((item, index) => [
      index + 1,
      formatDateTime(item.startTime),
      formatDateTime(item.endTime),
      item.duration.toFixed(2),
      item.area,
      item.shift,
      item.lineNumber
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `downtime_report_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const styles = {
    container: {
      padding: '30px',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e8edf2 100%)',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    },
    header: {
      textAlign: 'center',
      marginBottom: '30px',
    },
    title: {
      fontSize: '36px',
      fontWeight: '800',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      marginBottom: '10px',
      letterSpacing: '-0.5px',
    },
    subtitle: {
      fontSize: '16px',
      color: '#64748b',
      fontWeight: '500',
    },
    refreshBtn: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: '12px 24px',
      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(59, 130, 246, 0.4)',
      letterSpacing: '0.3px',
      marginTop: '15px',
    },
    filtersCard: {
      background: 'white',
      borderRadius: '20px',
      padding: '25px',
      marginBottom: '25px',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
      border: '1px solid rgba(0, 0, 0, 0.05)',
    },
    filterHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '20px',
      paddingBottom: '12px',
      borderBottom: '3px solid',
      borderImage: 'linear-gradient(90deg, #2563eb, #7c3aed) 1',
    },
    filterTitle: {
      fontSize: '22px',
      fontWeight: '700',
      color: '#1e293b',
      margin: 0,
    },
    filtersGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      gap: '15px',
      marginBottom: '20px',
    },
    filterGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    label: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#475569',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      letterSpacing: '0.3px',
    },
    required: {
      color: '#ef4444',
      marginLeft: '4px',
    },
    labelHint: {
      fontSize: '12px',
      fontWeight: '400',
      color: '#94a3b8',
    },
    input: {
      padding: '10px 14px',
      border: '2px solid #e2e8f0',
      borderRadius: '10px',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'all 0.3s ease',
      outline: 'none',
      background: 'white',
    },
    loadingText: {
      fontSize: '12px',
      color: '#2563eb',
      fontWeight: '500',
      marginTop: '4px',
    },
    actionButtons: {
      display: 'flex',
      gap: '15px',
      marginTop: '25px',
      justifyContent: 'center',
      flexWrap: 'wrap',
    },
    applyBtn: {
      padding: '14px 32px',
      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)',
      letterSpacing: '0.3px',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '20px',
      marginBottom: '25px',
    },
    statCard: {
      background: 'white',
      borderRadius: '16px',
      padding: '24px',
      display: 'flex',
      alignItems: 'center',
      gap: '20px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
    },
    statIcon: {
      width: '56px',
      height: '56px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      flexShrink: 0,
    },
    statLabel: {
      fontSize: '14px',
      color: '#64748b',
      margin: '0 0 8px 0',
      fontWeight: '500',
    },
    statValue: {
      fontSize: '28px',
      fontWeight: '800',
      color: '#1e293b',
      margin: 0,
    },
    tableCard: {
      background: 'white',
      borderRadius: '16px',
      padding: '24px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
      marginBottom: '25px',
    },
    tableHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
    },
    tableTitle: {
      fontSize: '18px',
      fontWeight: '700',
      color: '#1e293b',
      margin: 0,
    },
    exportBtn: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 20px',
      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    tableWrapper: {
      overflowX: 'auto',
    },
    table: {
      width: '100%',
      borderCollapse: 'separate',
      borderSpacing: 0,
    },
    th: {
      textAlign: 'left',
      padding: '16px',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      fontWeight: '700',
      fontSize: '13px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    td: {
      padding: '16px',
      fontSize: '14px',
      color: '#1e293b',
      borderBottom: '1px solid #f1f5f9',
    },
    evenRow: {
      background: 'white',
    },
    oddRow: {
      background: '#f8fafc',
    },
    timeCell: {
      fontFamily: 'monospace',
      fontSize: '13px',
      color: '#64748b',
    },
    durationBadge: {
      display: 'inline-block',
      padding: '6px 12px',
      background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
      color: 'white',
      borderRadius: '8px',
      fontSize: '12px',
      fontWeight: '600',
    },
    shiftBadge: {
      display: 'inline-block',
      padding: '6px 12px',
      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      color: 'white',
      borderRadius: '8px',
      fontSize: '12px',
      fontWeight: '600',
    },
    chartsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))',
      gap: '20px',
      marginBottom: '25px',
    },
    chartCard: {
      background: 'white',
      borderRadius: '16px',
      padding: '24px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
    },
    chartTitle: {
      fontSize: '18px',
      fontWeight: '700',
      color: '#1e293b',
      marginBottom: '20px',
    },
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px 20px',
      color: '#64748b',
    },
    spinner: {
      width: '40px',
      height: '40px',
      border: '4px solid #e2e8f0',
      borderTop: '4px solid #3b82f6',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      marginBottom: '16px',
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px 20px',
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderRadius: '15px',
      border: '2px dashed #cbd5e1',
    },
    emptyText: {
      marginTop: '16px',
      color: '#64748b',
      fontSize: '16px',
      fontWeight: '600',
    },
    emptySubtext: {
      marginTop: '8px',
      color: '#94a3b8',
      fontSize: '14px',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Production Downtime Viewer</h1>
        <p style={styles.subtitle}>Monitor and analyze production downtime incidents</p>
        <button 
          style={styles.refreshBtn} 
          onClick={fetchDowntimeData} 
          disabled={loading.data}
          onMouseEnter={(e) => {
            if (!loading.data) {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.5)';
            }
          }}
          onMouseLeave={(e) => {
            if (!loading.data) {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 15px rgba(59, 130, 246, 0.4)';
            }
          }}
        >
          <RefreshCw size={18} style={{ animation: loading.data ? 'spin 1s linear infinite' : 'none' }} />
          {loading.data ? 'Loading...' : 'Refresh Data'}
        </button>
      </div>

      <div style={styles.filtersCard}>
        <div style={styles.filterHeader}>
          <Filter size={20} />
          <h3 style={styles.filterTitle}>Filter Parameters</h3>
        </div>
        
        <div style={styles.filtersGrid}>
          <div style={styles.filterGroup}>
            <label style={styles.label}>
              Plant <span style={styles.required}>*</span>
            </label>
            <select
              style={styles.input}
              value={filters.serverId}
              onChange={(e) => handleFilterChange('serverId', e.target.value)}
            >
              <option value="">Select plant</option>
              {ServerInfo.map(server => (
                <option key={server.id} value={server.id}>
                  {server.name}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.label}>
              Area <span style={styles.required}>*</span>
            </label>
            <select
              style={styles.input}
              value={filters.area}
              onChange={(e) => handleFilterChange('area', e.target.value)}
            >
              <option value="">Select area</option>
              {areaOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.label}>
              Start Date <span style={styles.required}>*</span>
            </label>
            <input
              type="date"
              style={styles.input}
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
            />
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.label}>
              End Date <span style={styles.required}>*</span>
            </label>
            <input
              type="date"
              style={styles.input}
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
            />
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.label}>
              Project <span style={styles.required}>*</span>
            </label>
            <select
              style={styles.input}
              value={filters.projCode}
              onChange={(e) => handleFilterChange('projCode', e.target.value)}
              disabled={loading.projects || projectList.length === 0}
            >
              <option value="">
                {loading.projects ? 'Loading projects...' : 
                 projectList.length === 0 ? 'Select plant & area first' : 
                 'Select project'}
              </option>
              {projectList.map((project, index) => {
                const value = getValueForSubmission(project, 'projCode', 'projectCode', 'code', 'id');
                const display = getDisplayValue(project, 'projName', 'projectName', 'name', 'projCode', 'projectCode');
                return (
                  <option key={index} value={value}>
                    {display || 'Unknown Project'}
                  </option>
                );
              })}
            </select>
            {loading.projects && (
              <span style={styles.loadingText}>⏳ Loading...</span>
            )}
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.label}>
              Line Number <span style={styles.required}>*</span>
            </label>
            <select
              style={styles.input}
              value={filters.lineNumber}
              onChange={(e) => handleFilterChange('lineNumber', e.target.value)}
              disabled={loading.lines || lineList.length === 0}
            >
              <option value="">
                {loading.lines ? 'Loading lines...' : 
                 lineList.length === 0 ? 'Select project first' : 
                 'Select line'}
              </option>
              {lineList.map((line, index) => {
                const value = getValueForSubmission(line, 'lineNo', 'lineName', 'line', 'name', 'userId', 'id');
                const display = getDisplayValue(line, 'lineNo', 'lineName', 'line', 'name', 'userName', 'userId');
                return (
                  <option key={index} value={value}>
                    {display || 'Unknown Line'}
                  </option>
                );
              })}
            </select>
            {loading.lines && (
              <span style={styles.loadingText}>⏳ Loading...</span>
            )}
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.label}>
              Shift <span style={styles.required}>*</span>
            </label>
            <select
              style={styles.input}
              value={filters.shift}
              onChange={(e) => handleFilterChange('shift', e.target.value)}
            >
              <option value="">Select shift</option>
              {shiftOptions.map(shift => (
                <option key={shift} value={shift}>{shift}</option>
              ))}
            </select>
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.label}>
              Min Gap (minutes)
              <span style={styles.labelHint}>Default: 5 min</span>
            </label>
            <input
              type="number"
              style={styles.input}
              value={filters.minGapMinutes}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '' || (parseInt(val) > 0 && parseInt(val) <= 999)) {
                  handleFilterChange('minGapMinutes', val);
                }
              }}
              placeholder="5 (default)"
              min="1"
            />
          </div>
        </div>

        <div style={styles.actionButtons}>
          <button 
            style={styles.applyBtn} 
            onClick={fetchDowntimeData} 
            disabled={loading.data}
            onMouseEnter={(e) => {
              if (!loading.data) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.5)';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading.data) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(16, 185, 129, 0.4)';
              }
            }}
          >
            {loading.data ? '⏳ Loading...' : '🔍 Apply Filters'}
          </button>
        </div>
      </div>

      {downtimeData.length > 0 && (
        <div style={styles.statsGrid}>
          <div style={{ ...styles.statCard, borderLeft: '4px solid #3b82f6' }}>
            <div style={styles.statIcon} className="stat-icon-blue">
              <Clock size={24} />
            </div>
            <div>
              <p style={styles.statLabel}>Total Downtime</p>
              <p style={styles.statValue}>{formatDuration(stats.totalDowntime)}</p>
            </div>
          </div>

          <div style={{ ...styles.statCard, borderLeft: '4px solid #10b981' }}>
            <div style={styles.statIcon} className="stat-icon-green">
              <AlertCircle size={24} />
            </div>
            <div>
              <p style={styles.statLabel}>Incidents</p>
              <p style={styles.statValue}>{stats.incidentCount}</p>
            </div>
          </div>

          <div style={{ ...styles.statCard, borderLeft: '4px solid #f59e0b' }}>
            <div style={styles.statIcon} className="stat-icon-yellow">
              <TrendingDown size={24} />
            </div>
            <div>
              <p style={styles.statLabel}>Avg Duration</p>
              <p style={styles.statValue}>{formatDuration(stats.avgDuration)}</p>
            </div>
          </div>

          <div style={{ ...styles.statCard, borderLeft: '4px solid #ef4444' }}>
            <div style={styles.statIcon} className="stat-icon-red">
              <Calendar size={24} />
            </div>
            <div>
              <p style={styles.statLabel}>Longest Downtime</p>
              <p style={styles.statValue}>{formatDuration(stats.longestDowntime)}</p>
            </div>
          </div>

          <div style={{ ...styles.statCard, borderLeft: '4px solid #8b5cf6' }}>
            <div style={styles.statIcon} className="stat-icon-purple">
              <Clock size={24} />
            </div>
            <div>
              <p style={styles.statLabel}>Total Time Frame</p>
              <p style={styles.statValue}>{formatDuration(stats.totalTimeFrameMinutes)}</p>
            </div>
          </div>

          <div style={{ ...styles.statCard, borderLeft: '4px solid #06b6d4' }}>
            <div style={styles.statIcon} className="stat-icon-cyan">
              <TrendingDown size={24} />
            </div>
            <div>
              <p style={styles.statLabel}>Time Frame Count</p>
              <p style={styles.statValue}>{stats.timeFrameCount}</p>
            </div>
          </div>
        </div>
      )}

      <div style={styles.tableCard}>
        <div style={styles.tableHeader}>
          <h3 style={styles.tableTitle}>Downtime Incidents ({downtimeData.length})</h3>
          <button 
            style={styles.exportBtn} 
            onClick={exportToCSV} 
            disabled={downtimeData.length === 0}
            onMouseEnter={(e) => {
              if (downtimeData.length > 0) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (downtimeData.length > 0) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }
            }}
          >
            <Download size={18} />
            Export CSV
          </button>
        </div>

        {loading.data ? (
          <div style={styles.loadingContainer}>
            <div style={styles.spinner}></div>
            <p>Loading downtime data...</p>
          </div>
        ) : downtimeData.length === 0 ? (
          <div style={styles.emptyState}>
            <AlertCircle size={48} color="#94a3b8" />
            <p style={styles.emptyText}>No downtime incidents found</p>
            <p style={styles.emptySubtext}>Select filters above and click "Apply Filters" to view data</p>
          </div>
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>#</th>
                  <th style={styles.th}>Start Time</th>
                  <th style={styles.th}>End Time</th>
                  <th style={styles.th}>Duration</th>
                  <th style={styles.th}>Area</th>
                  <th style={styles.th}>Shift</th>
                  <th style={styles.th}>Line</th>
                </tr>
              </thead>
              <tbody>
                {downtimeData.map((item, index) => (
                  <tr key={item.id} style={index % 2 === 0 ? styles.evenRow : styles.oddRow}>
                    <td style={styles.td}>{index + 1}</td>
                    <td style={styles.td}>
                      <span style={styles.timeCell}>{formatDateTime(item.startTime)}</span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.timeCell}>{formatDateTime(item.endTime)}</span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.durationBadge}>{item.duration.toFixed(2)} min</span>
                    </td>
                    <td style={styles.td}>{item.area}</td>
                    <td style={styles.td}>
                      <span style={styles.shiftBadge}>{item.shift}</span>
                    </td>
                    <td style={styles.td}>{item.lineNumber}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {downtimeData.length > 0 && (
        <div style={styles.chartsGrid}>
          <div style={styles.chartCard}>
            <h3 style={styles.chartTitle}>Downtime by Reason</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartDataByReason}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="reason" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                  formatter={(value) => formatDuration(value)}
                />
                <Bar dataKey="duration" fill="#3b82f6" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div style={styles.chartCard}>
            <h3 style={styles.chartTitle}>Downtime Distribution by Shift</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartDataByShift}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ shift, percent }) => `${shift}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="duration"
                >
                  {chartDataByShift.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatDuration(value)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .stat-icon-blue { background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); }
        .stat-icon-green { background: linear-gradient(135deg, #10b981 0%, #059669 100%); }
        .stat-icon-yellow { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); }
        .stat-icon-red { background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); }
        .stat-icon-purple { background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); }
        .stat-icon-cyan { background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%); }
      `}</style>
    </div>
  );
};

export default DowntimeViewer;





// import React, { useState, useEffect } from 'react';
// import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
// import { Clock, Calendar, TrendingDown, AlertCircle, Filter, Download, RefreshCw } from 'lucide-react';

// const FormApiService = {
//   //API_BASE_URL: 'https://apps.genus.in/GenOpsAPI/api',
//   API_BASE_URL: 'http://localhost:5134/api',

//   async getPCBLiveProjects(serverId, forDate) {
//     try {
//       const response = await fetch(
//         `${this.API_BASE_URL}/PCB/live-projects/daily?serverId=${serverId}&forDate=${forDate}`,
//         { headers: { 'accept': '*/*' } }
//       );
//       if (!response.ok) throw new Error('Failed to fetch PCB projects');
//       return await response.json();
//     } catch (error) {
//       console.error('Error fetching PCB projects:', error);
//       throw error;
//     }
//   },

//   async getPCBRunningLines(serverId, projCode, forDate) {
//     try {
//       const response = await fetch(
//         `${this.API_BASE_URL}/PCB/running-lines/daily?serverId=${serverId}&projCode=${projCode}&forDate=${forDate}`,
//         { headers: { 'accept': '*/*' } }
//       );
//       if (!response.ok) throw new Error('Failed to fetch PCB running lines');
//       return await response.json();
//     } catch (error) {
//       console.error('Error fetching PCB running lines:', error);
//       throw error;
//     }
//   },

//   async getEMALiveProjects(serverId) {
//     try {
//       const response = await fetch(
//         `${this.API_BASE_URL}/Setting/LiveProjectList?serverID=${serverId}`,
//         { headers: { 'accept': '*/*' } }
//       );
//       if (!response.ok) throw new Error('Failed to fetch EMA projects');
//       return await response.json();
//     } catch (error) {
//       console.error('Error fetching EMA projects:', error);
//       throw error;
//     }
//   },

//   async getEMARunningLines(serverId, projCode, fromDt, toDt) {
//     try {
//       const params = new URLSearchParams({
//         serverId: serverId.toString(),
//         projCode: projCode,
//         fromDt: fromDt,
//         toDt: toDt
//       });

//       const response = await fetch(
//         `${this.API_BASE_URL}/Setting/RunningLines?${params}`,
//         { headers: { 'accept': '*/*' } }
//       );
//       if (!response.ok) throw new Error('Failed to fetch EMA running lines');
//       return await response.json();
//     } catch (error) {
//       console.error('Error fetching EMA running lines:', error);
//       throw error;
//     }
//   },

//   async getProductionGapsRange(serverId, projCode, lineNumber, shift, startDate, endDate, area, minGapMinutes) {
//     try {
//       const params = new URLSearchParams({
//         serverId: serverId.toString(),
//         projCode: projCode,
//         lineNumber: lineNumber.toString(),
//         shift: shift,
//         startDate: startDate,
//         endDate: endDate,
//         area: area
//       });

//       if (minGapMinutes && parseInt(minGapMinutes) > 0) {
//         params.append('minGapMinutes', minGapMinutes.toString());
//       }

//       const response = await fetch(
//         `${this.API_BASE_URL}/EntryFrom/gaps/range?${params}`,
//         { headers: { 'accept': '*/*' } }
//       );
      
//       if (!response.ok) throw new Error('Failed to fetch production gaps');
//       return await response.json();
//     } catch (error) {
//       console.error('Error fetching production gaps:', error);
//       throw error;
//     }
//   }
// };

// const DowntimeViewer = () => {
//   const [filters, setFilters] = useState({
//     serverId: '',
//     projCode: '',
//     lineNumber: '',
//     shift: '',
//     startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
//     endDate: new Date().toISOString().split('T')[0],
//     area: '',
//     minGapMinutes: ''
//   });

//   const [downtimeData, setDowntimeData] = useState([]);
//   const [projectList, setProjectList] = useState([]);
//   const [lineList, setLineList] = useState([]);
  
//   const [loading, setLoading] = useState({
//     projects: false,
//     lines: false,
//     data: false
//   });

//   const [stats, setStats] = useState({
//     totalDowntime: 0,
//     incidentCount: 0,
//     avgDuration: 0,
//     longestDowntime: 0,
//     totalTimeFrameMinutes: 0,
//     timeFrameCount: 0
//   });

//   const ServerInfo = [
//     { id: 1, name: 'Genus RND', serverIP: '192.10.10.4' },
//     { id: 2, name: 'RCP Jaipur', serverIP: '10.141.61.40' },
//     { id: 3, name: 'HDR 1101', serverIP: '10.133.100.21' },
//     { id: 4, name: 'HDR 3000', serverIP: '10.134.1.21' },
//     { id: 5, name: 'HDR 1100', serverIP: '10.133.1.22' },
//     { id: 6, name: 'Guhawati', serverIP: '10.161.1.22' },
//   ];

//   const shiftOptions = ['A', 'B'];
//   const areaOptions = ['PCB', 'EMA'];

//   const getDisplayValue = (item, ...keys) => {
//     if (typeof item === 'string' || typeof item === 'number') return String(item);
//     if (typeof item === 'object' && item !== null) {
//       for (const key of keys) {
//         if (item[key] !== undefined && item[key] !== null) return String(item[key]);
//       }
//     }
//     return '';
//   };

//   const getValueForSubmission = (item, ...keys) => {
//     if (typeof item === 'string' || typeof item === 'number') return String(item);
//     if (typeof item === 'object' && item !== null) {
//       for (const key of keys) {
//         if (item[key] !== undefined && item[key] !== null) return String(item[key]);
//       }
//     }
//     return '';
//   };

//   useEffect(() => {
//     const fetchProjects = async () => {
//       if (!filters.serverId || !filters.area || !filters.startDate) {
//         setProjectList([]);
//         return;
//       }

//       setLoading(prev => ({ ...prev, projects: true }));
//       try {
//         let projects = [];
//         const serverId = parseInt(filters.serverId);
//         const forDate = filters.startDate.split('T')[0];

//         if (filters.area === 'PCB') {
//           projects = await FormApiService.getPCBLiveProjects(serverId, forDate);
//         } else if (filters.area === 'EMA') {
//           projects = await FormApiService.getEMALiveProjects(serverId);
//         }

//         setProjectList(Array.isArray(projects) ? projects : []);
//         setFilters(prev => ({ ...prev, projCode: '', lineNumber: '' }));
//         setLineList([]);
//       } catch (error) {
//         console.error('Error fetching projects:', error);
//         setProjectList([]);
//       } finally {
//         setLoading(prev => ({ ...prev, projects: false }));
//       }
//     };

//     fetchProjects();
//   }, [filters.serverId, filters.area, filters.startDate]);

//   useEffect(() => {
//     const fetchLines = async () => {
//       if (!filters.projCode || !filters.area || !filters.serverId || !filters.startDate || !filters.endDate) {
//         setLineList([]);
//         return;
//       }

//       setLoading(prev => ({ ...prev, lines: true }));
//       try {
//         let lines = [];
//         const serverId = parseInt(filters.serverId);
//         const fromDt = filters.startDate.split('T')[0];
//         const toDt = filters.endDate.split('T')[0];

//         if (filters.area === 'PCB') {
//           lines = await FormApiService.getPCBRunningLines(serverId, filters.projCode, fromDt);
//         } else if (filters.area === 'EMA') {
//           lines = await FormApiService.getEMARunningLines(serverId, filters.projCode, fromDt, toDt);
//         }

//         setLineList(Array.isArray(lines) ? lines : []);
//         setFilters(prev => ({ ...prev, lineNumber: '' }));
//       } catch (error) {
//         console.error('Error fetching lines:', error);
//         setLineList([]);
//       } finally {
//         setLoading(prev => ({ ...prev, lines: false }));
//       }
//     };

//     fetchLines();
//   }, [filters.projCode, filters.area, filters.serverId, filters.startDate, filters.endDate]);

//   const fetchDowntimeData = async () => {
//     if (!filters.serverId || !filters.projCode || !filters.lineNumber || 
//         !filters.shift || !filters.startDate || !filters.endDate || !filters.area) {
//       alert('Please fill all required fields before fetching data');
//       return;
//     }

//     setLoading(prev => ({ ...prev, data: true }));
    
//     try {
//       const serverId = parseInt(filters.serverId);
//       const lineNumber = parseInt(filters.lineNumber);
//       const minGap = filters.minGapMinutes === '' ? '' : Math.max(1, parseInt(filters.minGapMinutes) || 5);

//       const data = await FormApiService.getProductionGapsRange(
//         serverId,
//         filters.projCode,
//         lineNumber,
//         filters.shift,
//         filters.startDate,
//         filters.endDate,
//         filters.area,
//         minGap
//       );
      
//       // Debug: Log the actual API response to see field names
//       console.log('API Response:', data);
//       if (data && data.length > 0) {
//         console.log('First item structure:', data[0]);
//         console.log('Available keys:', Object.keys(data[0]));
//       }
      
//       const processedData = Array.isArray(data)
//         ? data.map((item, index) => {
//             let startTime = null;
//             let endTime = null;

//             // API format: timeRange = "09:08 - 09:14", dayLabel = "2025-12-23"
//             if (item.timeRange && item.dayLabel) {
//               const parts = item.timeRange.split(' - ');
//               if (parts.length === 2) {
//                 startTime = `${item.dayLabel} ${parts[0]}`;
//                 endTime   = `${item.dayLabel} ${parts[1]}`;
//               }
//             }

//             const duration = parseFloat(item.gapMinutes || 0);

//             console.log(`Item ${index + 1}:`, {
//               startTime,
//               endTime,
//               duration,
//               rawItem: item
//             });

//             return {
//               id: index + 1,
//               startTime,
//               endTime,
//               duration,
//               reason: item.reason || item.Reason || 'Downtime',
//               category: item.category || item.Category || 'Unknown',
//               area: filters.area,
//               shift: filters.shift,
//               lineNumber
//             };
//           })
//         : [];

//       setDowntimeData(processedData);

      
//       const totalMinutes = processedData.reduce((sum, item) => sum + item.duration, 0);
//       const maxDuration = Math.max(...processedData.map(item => item.duration), 0);
      
//       setStats({
//         totalDowntime: totalMinutes,
//         incidentCount: processedData.length,
//         avgDuration: processedData.length > 0 ? Math.round(totalMinutes / processedData.length) : 0,
//         longestDowntime: maxDuration,
//         totalTimeFrameMinutes: totalMinutes,
//         timeFrameCount: processedData.length
//       });
      
//     } catch (error) {
//       console.error('Error fetching downtime data:', error);
//       alert(`Error fetching downtime data: ${error.message}`);
//       setDowntimeData([]);
//     } finally {
//       setLoading(prev => ({ ...prev, data: false }));
//     }
//   };

//   const handleFilterChange = (field, value) => {
//     setFilters(prev => ({ ...prev, [field]: value }));
//   };

//   const formatDuration = (minutes) => {
//     const hours = Math.floor(minutes / 60);
//     const mins = Math.round(minutes % 60);
//     return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
//   };

//   const formatDateTime = (isoString) => {
//     if (!isoString) return 'N/A';
//     try {
//       const date = new Date(isoString);
//       if (isNaN(date.getTime())) return 'N/A';
//       return date.toLocaleString('en-US', { 
//         month: 'short', 
//         day: 'numeric',
//         year: 'numeric',
//         hour: '2-digit', 
//         minute: '2-digit' 
//       });
//     } catch (e) {
//       return 'N/A';
//     }
//   };

//   const chartDataByReason = downtimeData.reduce((acc, item) => {
//     const existing = acc.find(x => x.reason === item.reason);
//     if (existing) {
//       existing.duration += item.duration;
//       existing.count += 1;
//     } else {
//       acc.push({ reason: item.reason, duration: item.duration, count: 1 });
//     }
//     return acc;
//   }, []);

//   const chartDataByShift = downtimeData.reduce((acc, item) => {
//     const existing = acc.find(x => x.shift === item.shift);
//     if (existing) {
//       existing.duration += item.duration;
//     } else {
//       acc.push({ shift: item.shift, duration: item.duration });
//     }
//     return acc;
//   }, []);

//   const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

//   const exportToCSV = () => {
//     if (downtimeData.length === 0) {
//       alert('No data to export');
//       return;
//     }

//     const headers = ['#', 'Start Time', 'End Time', 'Duration (min)', 'Area', 'Shift', 'Line'];
//     const rows = downtimeData.map((item, index) => [
//       index + 1,
//       formatDateTime(item.startTime),
//       formatDateTime(item.endTime),
//       item.duration.toFixed(2),
//       item.area,
//       item.shift,
//       item.lineNumber
//     ]);

//     const csvContent = [
//       headers.join(','),
//       ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
//     ].join('\n');

//     const blob = new Blob([csvContent], { type: 'text/csv' });
//     const url = window.URL.createObjectURL(blob);
//     const a = document.createElement('a');
//     a.href = url;
//     a.download = `downtime_report_${new Date().toISOString().slice(0, 10)}.csv`;
//     document.body.appendChild(a);
//     a.click();
//     document.body.removeChild(a);
//     window.URL.revokeObjectURL(url);
//   };

//   const styles = {
//     container: {
//       padding: '30px',
//       minHeight: '100vh',
//       background: 'linear-gradient(135deg, #f5f7fa 0%, #e8edf2 100%)',
//       fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
//     },
//     header: {
//       textAlign: 'center',
//       marginBottom: '30px',
//     },
//     title: {
//       fontSize: '36px',
//       fontWeight: '800',
//       background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
//       WebkitBackgroundClip: 'text',
//       WebkitTextFillColor: 'transparent',
//       backgroundClip: 'text',
//       marginBottom: '10px',
//       letterSpacing: '-0.5px',
//     },
//     subtitle: {
//       fontSize: '16px',
//       color: '#64748b',
//       fontWeight: '500',
//     },
//     refreshBtn: {
//       display: 'inline-flex',
//       alignItems: 'center',
//       gap: '8px',
//       padding: '12px 24px',
//       background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
//       color: 'white',
//       border: 'none',
//       borderRadius: '12px',
//       fontSize: '14px',
//       fontWeight: '600',
//       cursor: 'pointer',
//       transition: 'all 0.3s ease',
//       boxShadow: '0 4px 15px rgba(59, 130, 246, 0.4)',
//       letterSpacing: '0.3px',
//       marginTop: '15px',
//     },
//     filtersCard: {
//       background: 'white',
//       borderRadius: '20px',
//       padding: '25px',
//       marginBottom: '25px',
//       boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
//       border: '1px solid rgba(0, 0, 0, 0.05)',
//     },
//     filterHeader: {
//       display: 'flex',
//       alignItems: 'center',
//       gap: '12px',
//       marginBottom: '20px',
//       paddingBottom: '12px',
//       borderBottom: '3px solid',
//       borderImage: 'linear-gradient(90deg, #2563eb, #7c3aed) 1',
//     },
//     filterTitle: {
//       fontSize: '22px',
//       fontWeight: '700',
//       color: '#1e293b',
//       margin: 0,
//     },
//     filtersGrid: {
//       display: 'grid',
//       gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
//       gap: '15px',
//       marginBottom: '20px',
//     },
//     filterGroup: {
//       display: 'flex',
//       flexDirection: 'column',
//       gap: '8px',
//     },
//     label: {
//       fontSize: '14px',
//       fontWeight: '600',
//       color: '#475569',
//       display: 'flex',
//       justifyContent: 'space-between',
//       alignItems: 'center',
//       letterSpacing: '0.3px',
//     },
//     required: {
//       color: '#ef4444',
//       marginLeft: '4px',
//     },
//     labelHint: {
//       fontSize: '12px',
//       fontWeight: '400',
//       color: '#94a3b8',
//     },
//     input: {
//       padding: '10px 14px',
//       border: '2px solid #e2e8f0',
//       borderRadius: '10px',
//       fontSize: '14px',
//       fontWeight: '500',
//       transition: 'all 0.3s ease',
//       outline: 'none',
//       background: 'white',
//     },
//     loadingText: {
//       fontSize: '12px',
//       color: '#2563eb',
//       fontWeight: '500',
//       marginTop: '4px',
//     },
//     actionButtons: {
//       display: 'flex',
//       gap: '15px',
//       marginTop: '25px',
//       justifyContent: 'center',
//       flexWrap: 'wrap',
//     },
//     applyBtn: {
//       padding: '14px 32px',
//       background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
//       color: 'white',
//       border: 'none',
//       borderRadius: '12px',
//       fontSize: '16px',
//       fontWeight: '600',
//       cursor: 'pointer',
//       transition: 'all 0.3s ease',
//       boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)',
//       letterSpacing: '0.3px',
//     },
//     statsGrid: {
//       display: 'grid',
//       gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
//       gap: '20px',
//       marginBottom: '25px',
//     },
//     statCard: {
//       background: 'white',
//       borderRadius: '16px',
//       padding: '24px',
//       display: 'flex',
//       alignItems: 'center',
//       gap: '20px',
//       boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
//     },
//     statIcon: {
//       width: '56px',
//       height: '56px',
//       borderRadius: '12px',
//       display: 'flex',
//       alignItems: 'center',
//       justifyContent: 'center',
//       color: 'white',
//       flexShrink: 0,
//     },
//     statLabel: {
//       fontSize: '14px',
//       color: '#64748b',
//       margin: '0 0 8px 0',
//       fontWeight: '500',
//     },
//     statValue: {
//       fontSize: '28px',
//       fontWeight: '800',
//       color: '#1e293b',
//       margin: 0,
//     },
//     tableCard: {
//       background: 'white',
//       borderRadius: '16px',
//       padding: '24px',
//       boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
//       marginBottom: '25px',
//     },
//     tableHeader: {
//       display: 'flex',
//       justifyContent: 'space-between',
//       alignItems: 'center',
//       marginBottom: '20px',
//     },
//     tableTitle: {
//       fontSize: '18px',
//       fontWeight: '700',
//       color: '#1e293b',
//       margin: 0,
//     },
//     exportBtn: {
//       display: 'flex',
//       alignItems: 'center',
//       gap: '8px',
//       padding: '10px 20px',
//       background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
//       color: 'white',
//       border: 'none',
//       borderRadius: '8px',
//       fontSize: '14px',
//       fontWeight: '600',
//       cursor: 'pointer',
//       transition: 'all 0.3s ease',
//     },
//     tableWrapper: {
//       overflowX: 'auto',
//     },
//     table: {
//       width: '100%',
//       borderCollapse: 'separate',
//       borderSpacing: 0,
//     },
//     th: {
//       textAlign: 'left',
//       padding: '16px',
//       background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
//       color: 'white',
//       fontWeight: '700',
//       fontSize: '13px',
//       textTransform: 'uppercase',
//       letterSpacing: '0.5px',
//     },
//     td: {
//       padding: '16px',
//       fontSize: '14px',
//       color: '#1e293b',
//       borderBottom: '1px solid #f1f5f9',
//     },
//     evenRow: {
//       background: 'white',
//     },
//     oddRow: {
//       background: '#f8fafc',
//     },
//     timeCell: {
//       fontFamily: 'monospace',
//       fontSize: '13px',
//       color: '#64748b',
//     },
//     durationBadge: {
//       display: 'inline-block',
//       padding: '6px 12px',
//       background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
//       color: 'white',
//       borderRadius: '8px',
//       fontSize: '12px',
//       fontWeight: '600',
//     },
//     shiftBadge: {
//       display: 'inline-block',
//       padding: '6px 12px',
//       background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
//       color: 'white',
//       borderRadius: '8px',
//       fontSize: '12px',
//       fontWeight: '600',
//     },
//     chartsGrid: {
//       display: 'grid',
//       gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))',
//       gap: '20px',
//       marginBottom: '25px',
//     },
//     chartCard: {
//       background: 'white',
//       borderRadius: '16px',
//       padding: '24px',
//       boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
//     },
//     chartTitle: {
//       fontSize: '18px',
//       fontWeight: '700',
//       color: '#1e293b',
//       marginBottom: '20px',
//     },
//     loadingContainer: {
//       display: 'flex',
//       flexDirection: 'column',
//       alignItems: 'center',
//       justifyContent: 'center',
//       padding: '60px 20px',
//       color: '#64748b',
//     },
//     spinner: {
//       width: '40px',
//       height: '40px',
//       border: '4px solid #e2e8f0',
//       borderTop: '4px solid #3b82f6',
//       borderRadius: '50%',
//       animation: 'spin 1s linear infinite',
//       marginBottom: '16px',
//     },
//     emptyState: {
//       display: 'flex',
//       flexDirection: 'column',
//       alignItems: 'center',
//       justifyContent: 'center',
//       padding: '60px 20px',
//       background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
//       borderRadius: '15px',
//       border: '2px dashed #cbd5e1',
//     },
//     emptyText: {
//       marginTop: '16px',
//       color: '#64748b',
//       fontSize: '16px',
//       fontWeight: '600',
//     },
//     emptySubtext: {
//       marginTop: '8px',
//       color: '#94a3b8',
//       fontSize: '14px',
//     },
//   };

//   return (
//     <div style={styles.container}>
//       <div style={styles.header}>
//         <h1 style={styles.title}>Production Downtime Viewer</h1>
//         <p style={styles.subtitle}>Monitor and analyze production downtime incidents</p>
//         <button 
//           style={styles.refreshBtn} 
//           onClick={fetchDowntimeData} 
//           disabled={loading.data}
//           onMouseEnter={(e) => {
//             if (!loading.data) {
//               e.target.style.transform = 'translateY(-2px)';
//               e.target.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.5)';
//             }
//           }}
//           onMouseLeave={(e) => {
//             if (!loading.data) {
//               e.target.style.transform = 'translateY(0)';
//               e.target.style.boxShadow = '0 4px 15px rgba(59, 130, 246, 0.4)';
//             }
//           }}
//         >
//           <RefreshCw size={18} style={{ animation: loading.data ? 'spin 1s linear infinite' : 'none' }} />
//           {loading.data ? 'Loading...' : 'Refresh Data'}
//         </button>
//       </div>

//       <div style={styles.filtersCard}>
//         <div style={styles.filterHeader}>
//           <Filter size={20} />
//           <h3 style={styles.filterTitle}>Filter Parameters</h3>
//         </div>
        
//         <div style={styles.filtersGrid}>
//           <div style={styles.filterGroup}>
//             <label style={styles.label}>
//               Plant <span style={styles.required}>*</span>
//             </label>
//             <select
//               style={styles.input}
//               value={filters.serverId}
//               onChange={(e) => handleFilterChange('serverId', e.target.value)}
//             >
//               <option value="">Select plant</option>
//               {ServerInfo.map(server => (
//                 <option key={server.id} value={server.id}>
//                   {server.name}
//                 </option>
//               ))}
//             </select>
//           </div>

//           <div style={styles.filterGroup}>
//             <label style={styles.label}>
//               Area <span style={styles.required}>*</span>
//             </label>
//             <select
//               style={styles.input}
//               value={filters.area}
//               onChange={(e) => handleFilterChange('area', e.target.value)}
//             >
//               <option value="">Select area</option>
//               {areaOptions.map(option => (
//                 <option key={option} value={option}>{option}</option>
//               ))}
//             </select>
//           </div>

//           <div style={styles.filterGroup}>
//             <label style={styles.label}>
//               Start Date <span style={styles.required}>*</span>
//             </label>
//             <input
//               type="date"
//               style={styles.input}
//               value={filters.startDate}
//               onChange={(e) => handleFilterChange('startDate', e.target.value)}
//             />
//           </div>

//           <div style={styles.filterGroup}>
//             <label style={styles.label}>
//               End Date <span style={styles.required}>*</span>
//             </label>
//             <input
//               type="date"
//               style={styles.input}
//               value={filters.endDate}
//               onChange={(e) => handleFilterChange('endDate', e.target.value)}
//             />
//           </div>

//           <div style={styles.filterGroup}>
//             <label style={styles.label}>
//               Project <span style={styles.required}>*</span>
//             </label>
//             <select
//               style={styles.input}
//               value={filters.projCode}
//               onChange={(e) => handleFilterChange('projCode', e.target.value)}
//               disabled={loading.projects || projectList.length === 0}
//             >
//               <option value="">
//                 {loading.projects ? 'Loading projects...' : 
//                  projectList.length === 0 ? 'Select plant & area first' : 
//                  'Select project'}
//               </option>
//               {projectList.map((project, index) => {
//                 const value = getValueForSubmission(project, 'projCode', 'projectCode', 'code', 'id');
//                 const display = getDisplayValue(project, 'projName', 'projectName', 'name', 'projCode', 'projectCode');
//                 return (
//                   <option key={index} value={value}>
//                     {display || 'Unknown Project'}
//                   </option>
//                 );
//               })}
//             </select>
//             {loading.projects && (
//               <span style={styles.loadingText}>⏳ Loading...</span>
//             )}
//           </div>

//           <div style={styles.filterGroup}>
//             <label style={styles.label}>
//               Line Number <span style={styles.required}>*</span>
//             </label>
//             <select
//               style={styles.input}
//               value={filters.lineNumber}
//               onChange={(e) => handleFilterChange('lineNumber', e.target.value)}
//               disabled={loading.lines || lineList.length === 0}
//             >
//               <option value="">
//                 {loading.lines ? 'Loading lines...' : 
//                  lineList.length === 0 ? 'Select project first' : 
//                  'Select line'}
//               </option>
//               {lineList.map((line, index) => {
//                 const value = getValueForSubmission(line, 'lineNo', 'lineName', 'line', 'name', 'userId', 'id');
//                 const display = getDisplayValue(line, 'lineNo', 'lineName', 'line', 'name', 'userName', 'userId');
//                 return (
//                   <option key={index} value={value}>
//                     {display || 'Unknown Line'}
//                   </option>
//                 );
//               })}
//             </select>
//             {loading.lines && (
//               <span style={styles.loadingText}>⏳ Loading...</span>
//             )}
//           </div>

//           <div style={styles.filterGroup}>
//             <label style={styles.label}>
//               Shift <span style={styles.required}>*</span>
//             </label>
//             <select
//               style={styles.input}
//               value={filters.shift}
//               onChange={(e) => handleFilterChange('shift', e.target.value)}
//             >
//               <option value="">Select shift</option>
//               {shiftOptions.map(shift => (
//                 <option key={shift} value={shift}>{shift}</option>
//               ))}
//             </select>
//           </div>

//           <div style={styles.filterGroup}>
//             <label style={styles.label}>
//               Min Gap (minutes)
//               <span style={styles.labelHint}>Default: 5 min</span>
//             </label>
//             <input
//               type="number"
//               style={styles.input}
//               value={filters.minGapMinutes}
//               onChange={(e) => {
//                 const val = e.target.value;
//                 if (val === '' || (parseInt(val) > 0 && parseInt(val) <= 999)) {
//                   handleFilterChange('minGapMinutes', val);
//                 }
//               }}
//               placeholder="5 (default)"
//               min="1"
//             />
//           </div>
//         </div>

//         <div style={styles.actionButtons}>
//           <button 
//             style={styles.applyBtn} 
//             onClick={fetchDowntimeData} 
//             disabled={loading.data}
//             onMouseEnter={(e) => {
//               if (!loading.data) {
//                 e.target.style.transform = 'translateY(-2px)';
//                 e.target.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.5)';
//               }
//             }}
//             onMouseLeave={(e) => {
//               if (!loading.data) {
//                 e.target.style.transform = 'translateY(0)';
//                 e.target.style.boxShadow = '0 4px 15px rgba(16, 185, 129, 0.4)';
//               }
//             }}
//           >
//             {loading.data ? '⏳ Loading...' : '🔍 Apply Filters'}
//           </button>
//         </div>
//       </div>

//       {downtimeData.length > 0 && (
//         <div style={styles.statsGrid}>
//           <div style={{ ...styles.statCard, borderLeft: '4px solid #3b82f6' }}>
//             <div style={styles.statIcon} className="stat-icon-blue">
//               <Clock size={24} />
//             </div>
//             <div>
//               <p style={styles.statLabel}>Total Downtime</p>
//               <p style={styles.statValue}>{formatDuration(stats.totalDowntime)}</p>
//             </div>
//           </div>

//           <div style={{ ...styles.statCard, borderLeft: '4px solid #10b981' }}>
//             <div style={styles.statIcon} className="stat-icon-green">
//               <AlertCircle size={24} />
//             </div>
//             <div>
//               <p style={styles.statLabel}>Incidents</p>
//               <p style={styles.statValue}>{stats.incidentCount}</p>
//             </div>
//           </div>

//           <div style={{ ...styles.statCard, borderLeft: '4px solid #f59e0b' }}>
//             <div style={styles.statIcon} className="stat-icon-yellow">
//               <TrendingDown size={24} />
//             </div>
//             <div>
//               <p style={styles.statLabel}>Avg Duration</p>
//               <p style={styles.statValue}>{formatDuration(stats.avgDuration)}</p>
//             </div>
//           </div>

//           <div style={{ ...styles.statCard, borderLeft: '4px solid #ef4444' }}>
//             <div style={styles.statIcon} className="stat-icon-red">
//               <Calendar size={24} />
//             </div>
//             <div>
//               <p style={styles.statLabel}>Longest Downtime</p>
//               <p style={styles.statValue}>{formatDuration(stats.longestDowntime)}</p>
//             </div>
//           </div>

//           <div style={{ ...styles.statCard, borderLeft: '4px solid #8b5cf6' }}>
//             <div style={styles.statIcon} className="stat-icon-purple">
//               <Clock size={24} />
//             </div>
//             <div>
//               <p style={styles.statLabel}>Total Time Frame</p>
//               <p style={styles.statValue}>{formatDuration(stats.totalTimeFrameMinutes)}</p>
//             </div>
//           </div>

//           <div style={{ ...styles.statCard, borderLeft: '4px solid #06b6d4' }}>
//             <div style={styles.statIcon} className="stat-icon-cyan">
//               <TrendingDown size={24} />
//             </div>
//             <div>
//               <p style={styles.statLabel}>Time Frame Count</p>
//               <p style={styles.statValue}>{stats.timeFrameCount}</p>
//             </div>
//           </div>
//         </div>
//       )}

//       <div style={styles.tableCard}>
//         <div style={styles.tableHeader}>
//           <h3 style={styles.tableTitle}>Downtime Incidents ({downtimeData.length})</h3>
//           <button 
//             style={styles.exportBtn} 
//             onClick={exportToCSV} 
//             disabled={downtimeData.length === 0}
//             onMouseEnter={(e) => {
//               if (downtimeData.length > 0) {
//                 e.target.style.transform = 'translateY(-2px)';
//                 e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
//               }
//             }}
//             onMouseLeave={(e) => {
//               if (downtimeData.length > 0) {
//                 e.target.style.transform = 'translateY(0)';
//                 e.target.style.boxShadow = 'none';
//               }
//             }}
//           >
//             <Download size={18} />
//             Export CSV
//           </button>
//         </div>

//         {loading.data ? (
//           <div style={styles.loadingContainer}>
//             <div style={styles.spinner}></div>
//             <p>Loading downtime data...</p>
//           </div>
//         ) : downtimeData.length === 0 ? (
//           <div style={styles.emptyState}>
//             <AlertCircle size={48} color="#94a3b8" />
//             <p style={styles.emptyText}>No downtime incidents found</p>
//             <p style={styles.emptySubtext}>Select filters above and click "Apply Filters" to view data</p>
//           </div>
//         ) : (
//           <div style={styles.tableWrapper}>
//             <table style={styles.table}>
//               <thead>
//                 <tr>
//                   <th style={styles.th}>#</th>
//                   <th style={styles.th}>Start Time</th>
//                   <th style={styles.th}>End Time</th>
//                   <th style={styles.th}>Duration</th>
//                   <th style={styles.th}>Area</th>
//                   <th style={styles.th}>Shift</th>
//                   <th style={styles.th}>Line</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {downtimeData.map((item, index) => (
//                   <tr key={item.id} style={index % 2 === 0 ? styles.evenRow : styles.oddRow}>
//                     <td style={styles.td}>{index + 1}</td>
//                     <td style={styles.td}>
//                       <span style={styles.timeCell}>{formatDateTime(item.startTime)}</span>
//                     </td>
//                     <td style={styles.td}>
//                       <span style={styles.timeCell}>{formatDateTime(item.endTime)}</span>
//                     </td>
//                     <td style={styles.td}>
//                       <span style={styles.durationBadge}>{item.duration.toFixed(2)} min</span>
//                     </td>
//                     <td style={styles.td}>{item.area}</td>
//                     <td style={styles.td}>
//                       <span style={styles.shiftBadge}>{item.shift}</span>
//                     </td>
//                     <td style={styles.td}>{item.lineNumber}</td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>
//         )}
//       </div>

//       {downtimeData.length > 0 && (
//         <div style={styles.chartsGrid}>
//           <div style={styles.chartCard}>
//             <h3 style={styles.chartTitle}>Downtime by Reason</h3>
//             <ResponsiveContainer width="100%" height={300}>
//               <BarChart data={chartDataByReason}>
//                 <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
//                 <XAxis dataKey="reason" tick={{ fontSize: 12 }} />
//                 <YAxis tick={{ fontSize: 12 }} />
//                 <Tooltip 
//                   contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
//                   formatter={(value) => formatDuration(value)}
//                 />
//                 <Bar dataKey="duration" fill="#3b82f6" radius={[8, 8, 0, 0]} />
//               </BarChart>
//             </ResponsiveContainer>
//           </div>

//           <div style={styles.chartCard}>
//             <h3 style={styles.chartTitle}>Downtime Distribution by Shift</h3>
//             <ResponsiveContainer width="100%" height={300}>
//               <PieChart>
//                 <Pie
//                   data={chartDataByShift}
//                   cx="50%"
//                   cy="50%"
//                   labelLine={false}
//                   label={({ shift, percent }) => `${shift}: ${(percent * 100).toFixed(0)}%`}
//                   outerRadius={100}
//                   fill="#8884d8"
//                   dataKey="duration"
//                 >
//                   {chartDataByShift.map((entry, index) => (
//                     <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
//                   ))}
//                 </Pie>
//                 <Tooltip formatter={(value) => formatDuration(value)} />
//               </PieChart>
//             </ResponsiveContainer>
//           </div>
//         </div>
//       )}

//       <style>{`
//         @keyframes spin {
//           from { transform: rotate(0deg); }
//           to { transform: rotate(360deg); }
//         }
//         .stat-icon-blue { background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); }
//         .stat-icon-green { background: linear-gradient(135deg, #10b981 0%, #059669 100%); }
//         .stat-icon-yellow { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); }
//         .stat-icon-red { background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); }
//         .stat-icon-purple { background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); }
//         .stat-icon-cyan { background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%); }
//       `}</style>
//     </div>
//   );
// };

// export default DowntimeViewer;